<template>
  <div>
    <!-- Basic Info / Registration -->
    <b-card :title="$t('audit-finding.section.registration')" class="mb-3">
      <b-row>
        <b-col md="6">
          <b-form-group :label="$t('audit-control.finding.no')" v-if="form.no">
            <b-form-input v-model="form.no" disabled></b-form-input>
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group v-if="form._id" :label="$t('audit-finding.status.text')">
            <b-badge v-if="form.status" :variant="statusVariant" class="p-2">{{ $t(`audit-finding.status.${form.status.toLowerCase()}`) }}</b-badge>
          </b-form-group>
        </b-col>
      </b-row>
      <b-form-group>
        <template #label>{{ $t("audit-finding.name") }} <span class="text-danger">*</span></template>
        <sweet-input :disableValidation="true" v-model="form.name" />
      </b-form-group>
      <b-form-group>
        <template #label>{{ $t("audit-finding.description") }} <span class="text-danger">*</span></template>
        <sweet-textarea v-model="form.description" :rows="3" />
      </b-form-group>

      <b-row>
        <b-col md="4">
          <b-form-group>
            <template #label>{{ $t("audit-finding.register-date") }} <span class="text-danger">*</span></template>
            <b-form-datepicker v-model="form.registerDate" class="mb-2" disabled></b-form-datepicker>
          </b-form-group>
        </b-col>
        <b-col md="4">
          <b-form-group>
            <template #label>{{ $t("audit-finding.detection-date") }} <span class="text-danger">*</span></template>
            <b-form-datepicker v-model="form.detectionDate" class="mb-2"></b-form-datepicker>
          </b-form-group>
        </b-col>
        <b-col md="4">
          <b-form-group>
            <template #label>
              {{ $t("audit-finding.type.text") }}
              <right-panel
                class="ml-2 d-inline-block"
                @hidden="$refs.typeFilter && $refs.typeFilter.reset()"
                id="sidebar-new-finding-type"
                :text="$t('audit-finding.type.new')"
                width="600px"
                :icon="'mdi mdi-plus'"
              >
                <div class="pl-5 pr-5">
                  <FindingTypeForm />
                </div>
              </right-panel>
            </template>
            <filter-select
              ref="typeFilter"
              :selected="form.type"
              @filter="onTypeSearch"
              @changed="onTypeSelect"
              :placeholder="$t('audit-finding.type.text')"
              optionLabel="name"
              :multiple="false"
            />
          </b-form-group>
        </b-col>
      </b-row>

      <b-row>
        <b-col md="4">
          <b-form-group>
            <template #label>
              {{ $t("audit-finding.root-cause.text") }}
              <right-panel
                class="ml-2 d-inline-block"
                @hidden="$refs.rootCauseFilter && $refs.rootCauseFilter.reset()"
                id="sidebar-new-finding-root-cause"
                :text="$t('audit-finding.root-cause.new')"
                width="600px"
                :icon="'mdi mdi-plus'"
              >
                <div class="pl-5 pr-5">
                  <FindingRootCauseForm />
                </div>
              </right-panel>
            </template>
            <filter-select
              ref="rootCauseFilter"
              :selected="form.rootCauses"
              @filter="onRootCauseSearch"
              @changed="onRootCauseSelect"
              :placeholder="$t('audit-finding.root-cause.text')"
              optionLabel="name"
              :multiple="true"
            />
          </b-form-group>
        </b-col>
        <b-col md="4">
          <b-form-group>
            <template #label>
              {{ $t("audit-finding.category.text") }}
              <right-panel
                class="ml-2 d-inline-block"
                @hidden="$refs.categoryFilter && $refs.categoryFilter.reset()"
                id="sidebar-new-finding-category"
                :text="$t('audit-finding.category.new')"
                width="600px"
                :icon="'mdi mdi-plus'"
              >
                <div class="pl-5 pr-5">
                  <FindingCategoryForm />
                </div>
              </right-panel>
            </template>
            <filter-select
              ref="categoryFilter"
              :selected="form.category"
              @filter="onCategorySearch"
              @changed="onCategorySelect"
              :placeholder="$t('audit-finding.category.text')"
              optionLabel="name"
              :multiple="false"
            />
          </b-form-group>
        </b-col>
        <b-col md="4">
          <b-form-group class="mt-3" :label="$t('audit-finding.admin')">
            <organization-member-select :selected="form.admin" @onSelect="onAdminSelect" :organization="currentOrganization" />
          </b-form-group>
        </b-col>
      </b-row>

      <b-row>
        <b-col md="4">
          <div class="form-group">
            <label>{{ $t("audit-finding.level") }}</label>
            <select @change="ownerChanged" class="form-control" v-model="selectedOwnerType">
              <option value="ORGANIZATION">{{ $t("audit-control.control-level.corporate") }}</option>
              <option value="DEPARTMENT">{{ $t("department.text") }}</option>
              <option value="PROCESS">{{ $t("process.text") }}</option>
            </select>
          </div>
        </b-col>
        <b-col md="4">
          <div class="form-group">
            <div v-if="selectedOwnerType == 'ORGANIZATION'">
              <label>{{ $t("organization.text") }}</label>
              <filter-select
                disabled
                v-show="currentOrganization"
                :selected="{ _id: currentOrganization._id, name: { tr: currentOrganization.name, en: currentOrganization.name } }"
                :placeholder="$t('organization.text')"
                :multiple="false"
                :custom-label="customLabel"
              />
            </div>
            <div v-if="selectedOwnerType == 'DEPARTMENT'">
              <label>{{ $t("department.text") }}</label>
              <filter-select
                ref="departmentFilter"
                :selected="form.owner ? form.owner.department : null"
                :placeholder="$t('department.text')"
                :multiple="false"
                @filter="onDepartmentSearch"
                @changed="onDepartmentSelect"
              />
            </div>
            <div v-if="selectedOwnerType == 'PROCESS'">
              <label>{{ $t("process.text") }}</label>
              <filter-select
                ref="processFilter"
                @filter="onProcessSearch"
                :selected="form.owner ? form.owner.process : null"
                :placeholder="$t('process.text')"
                :multiple="false"
                @changed="onProcessSelect"
              />
            </div>
          </div>
        </b-col>
        <b-col md="4">
          <b-form-group :label="$t('audit-finding.class.text')">
            <b-form-select v-model="form.class" :options="findingClassOptions">
              <template #first>
                <b-form-select-option :value="null">{{ $t("common.select") }}</b-form-select-option>
              </template>
            </b-form-select>
          </b-form-group>
        </b-col>
      </b-row>

      <b-row>
        <b-col md="4">
          <b-form-group>
            <template #label>{{ $t("audit-finding.criticality") }} <span class="text-danger">*</span></template>
            <b-form-select v-model="selectedTargetIndex" :options="findingTargetOptions" @change="onCriticalityChange">
              <template #first>
                <b-form-select-option :value="null">{{ $t("common.select") }}</b-form-select-option>
              </template>
            </b-form-select>
          </b-form-group>
        </b-col>
      </b-row>

      <div class="row">
        <div class="col-md-12">
          <div class="form-group">
            <label>{{ $t("finding.form.control-text") }}</label>
            <b-button variant="light" size="sm" class="text-info font-weight-bold" @click="$bvModal.show('control-select')">
              <i class="bx bx-plus"></i> {{ $t("common.add") }}
            </b-button>

            <table class="table table-bordered table-striped ">
              <thead class="bg-soft-info">
                <tr>
                  <th>{{ $t("audit-control.control.no") }}</th>
                  <th>{{ $t("audit-control.control.name") }}</th>
                  <th style="width: 50px;"></th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="form.control" class="cursor-pointer">
                  <td>{{ form.control.no }}</td>
                  <td>{{ form.control.name | translate }}</td>
                  <td class="text-center" @click.stop>
                    <i class="bx bx-trash hand text-danger font-size-18" @click="form.control = undefined"></i>
                  </td>
                </tr>
                <tr v-if="!form.control">
                  <td colspan="5" class="text-center">{{ $t("common.no-data") }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-md-12">
          <div class="form-group">
            <label>{{ $t("audit-control.control.compliance-items") }}</label>

            <span v-b-modal="'compliance-item-modal-select'" class="text-primary">
              <i class="bx bx-search font-size-16 align-middle"></i>
              {{ $t("compliance-item.add") }}
            </span>
            <ComplianceItemSelect
              id="compliance-item-modal-select"
              multiple="true"
              :title="$t('compliance-item.add')"
              store="libraryComplianceItem"
              @selected="complianceItemSelected"
            />

            <table class="table table-bordered table-striped bg-white">
              <thead class="bg-secondary text-white">
                <tr>
                  <td class="w-10">{{ $t("compliance-item.compliance") }}</td>
                  <td class="w-10">{{ $t("finding.form.compliance-item-group-text") }}</td>
                  <td class="w-10">{{ $t("finding.form.compliance-item-no") }}</td>
                  <td class="w-10">{{ $t("finding.form.compliance-item-level") }}</td>
                  <td class="w-60">{{ $t("finding.form.compliance-item-name") }}</td>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in form.complianceItems" :key="item._id">
                  <td>{{ item.compliance }}</td>
                  <td>
                    <span v-if="item.group">{{ item.group.name | translate }}</span>
                  </td>
                  <td>{{ item.no }}</td>
                  <td>{{ item.level }}</td>
                  <td>{{ item.name | translate }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </b-card>
    <b-form-group :label="$t('audit-finding.suggestions')">
      <sweet-textarea v-model="form.suggestions" :rows="3" />
    </b-form-group>

    <div class="row mt-2">
      <div class="col-md-12">
        <b-form-group :label="$t('risk.text')">
          <sweet-textarea v-model="form.risk" :rows="3" />
        </b-form-group>
      </div>
    </div>

    <!-- Approval Info -->
    <b-card :title="$t('audit-finding.section.approval')" class="mb-3">
      <b-row>
        <b-col md="6">
          <b-form-group :label="$t('common.approver')">
            <b-form-input :value="expectedApproverName" disabled></b-form-input>
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group :label="$t('common.reporter-note')">
            <sweet-textarea v-model="form.reporterNote" :rows="3" />
          </b-form-group>
        </b-col>
      </b-row>

      <div v-if="form.approvals && form.approvals.length > 0">
        <h5 class="font-size-14 mb-3">{{ $t("audit-finding.approval-history") }}</h5>
        <div class="table-responsive">
          <table class="table table-bordered mb-0">
            <thead>
              <tr>
                <th>{{ $t("audit-finding.approval.status") }}</th>
                <th>{{ $t("audit-finding.approval.requested-by") }}</th>
                <th>{{ $t("audit-finding.approval.requested-at") }}</th>
                <th>{{ $t("audit-finding.approval.request-note") }}</th>
                <th>{{ $t("audit-finding.approval.responded-by") }}</th>
                <th>{{ $t("audit-finding.approval.responded-at") }}</th>
                <th>{{ $t("audit-finding.approval.response-note") }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(approval, index) in form.approvals" :key="index">
                <td>
                  <b-badge :variant="getApprovalStatusVariant(approval.status)">{{ $t("audit-finding.status." + approval.status.toLowerCase()) }}</b-badge>
                </td>
                <td>{{ approval.requestedBy.name }} {{ approval.requestedBy.surname }}</td>
                <td>{{ approval.requestedAt | date }}</td>
                <td>{{ approval.requestNote | translate }}</td>
                <td>
                  <span v-if="approval.respondedBy">{{ approval.respondedBy.name }} {{ approval.respondedBy.surname }}</span>
                </td>
                <td>{{ approval.respondedAt | date }}</td>
                <td>{{ approval.responseNote | translate }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </b-card>

    <!-- History -->
    <b-card :title="$t('audit-finding.section.history')" class="mb-3" v-if="form.history && form.history.length > 0">
      <organization-finding-history :history="form.history" />
    </b-card>

    <div class="text-right">
      <b-button variant="secondary" @click="$emit('cancel')" class="mr-2">{{ $t("common.cancel") }}</b-button>

      <b-button variant="primary" @click="save" class="mr-2">{{ $t("finding.save") }}</b-button>
    </div>
    <AuditControlSelectModal
      id="control-select"
      :title="$t('audit-control.control.add')"
      :hide-created-button="true"
      :add-button-text="$t('audit-control.select')"
      :pre-selected-ids="selectedControlIds"
      @selected="addControls"
      :limit="1"
    />
  </div>
</template>

<script>
import AuditControlSelectModal from "../audit-control/audit/audit-control-select-modal.vue";
import SweetInput from "@c/form/sweet-input";
import SweetTextarea from "@c/form/sweet-textarea";
import OrganizationMemberSelect from "@cc/organization-member-select";
import ComplianceItemSelect from "../audit-control/control/compliance-item-select.vue";
import OrganizationFindingHistory from "./organization-finding-history.vue";
import FindingTypeForm from "./library/workspace-library-finding-type-form";
import FindingRootCauseForm from "./library/workspace-library-finding-root-cause-form";
import FindingCategoryForm from "./library/workspace-library-finding-category-form";
import { mapGetters } from "vuex";
export default {
  components: {
    SweetInput,
    SweetTextarea,
    OrganizationMemberSelect,
    ComplianceItemSelect,
    OrganizationFindingHistory,
    FindingTypeForm,
    FindingRootCauseForm,
    FindingCategoryForm,
    AuditControlSelectModal,
  },
  props: {
    controlId: String,
    testId: String,
    finding: Object,
    initialControl: Object,
  },
  data() {
    return {
      form: {
        targetApprovalDate: undefined,
        targetResolutionDate: undefined,
        registerDate: new Date().toISOString().slice(0, 10),
        detectionDate: new Date().toISOString().slice(0, 10),
        informedPeople: [],
        complianceItems: [],
        control: undefined,
        class: undefined,
        owner: { organization: this.currentOrganization },
      },
      personSelectValue: undefined,
      selectedTargetIndex: undefined,
      selectedOwnerType: "ORGANIZATION",
    };
  },

  computed: {
    ...mapGetters({
      targetConfig: "organizationFindingTarget/selectedEntity",
    }),
    findingTargets() {
      return this.targetConfig?.criticality || {};
    },
    findingTargetOptions() {
      return ["LOW", "MEDIUM", "HIGH", "CRITICAL"].map((k) => ({
        value: k,
        text: this.$t("audit-control.criticality." + k.toLowerCase()),
      }));
    },
    selectedControlIds() {
      return this.form.control ? [this.form.control._id] : [];
    },
    editedEntity() {
      return this.$store.getters["organizationFinding/editedEntity"];
    },
    statusVariant() {
      switch (this.form.status) {
        case "AWAITING_APPROVAL":
          return "secondary";
        case "EVALUATING":
          return "primary";
        case "ACTION_PLAN_MONITORING":
          return "info";
        case "EXTENSION_APPROVAL":
          return "warning";
        case "COMPLETED":
          return "success";
        case "PASSIVE":
          return "secondary";
        case "CANCELED":
          return "danger";
        default:
          return "light";
      }
    },

    findingClassOptions() {
      return [
        { value: "NOTABLE_CONTROL_DEFICIENCY", text: this.$t("audit-finding.class-options.notable_control_deficiency") },
        { value: "SIGNIFICANT_CONTROL_DEFICIENCY", text: this.$t("audit-finding.class-options.significant_control_deficiency") },
        { value: "IMPROVEMENT_RECOMMENDATION", text: this.$t("audit-finding.class-options.improvement_recommendation") },
      ];
    },

    expectedApproverName() {
      if (this.selectedOwnerType === "ORGANIZATION") {
        return this.currentOrganization ? `${this.currentOrganization.createdBy.name} ${this.currentOrganization.createdBy.surname || ""}` : "-";
      } else if (this.selectedOwnerType === "DEPARTMENT" && this.form.owner && this.form.owner.department) {
        const dept = this.form.owner.department;
        if (dept.admins && dept.admins.length > 0) {
          return `${dept.admins[0].account.name} ${dept.admins[0].account.surname || ""}`;
        }
        return "-";
      } else if (this.selectedOwnerType === "PROCESS") {
        return this.currentOrganization ? `${this.currentOrganization.createdBy.name} ${this.currentOrganization.createdBy.surname || ""}` : "-";
      }
      return "-";
    },
    isCriticalityMandatory() {
      // Criticality is mandatory if status is NOT one of the initial/passive states
      // Typically: Mandatory in EVALUATING, ACTION_PLAN_MONITORING, COMPLETED, EXTENSION_APPROVAL
      const optionalStatuses = ["AWAITING_APPROVAL", "PENDING", "PASSIVE", "CANCELED"];
      return this.form.status && !optionalStatuses.includes(this.form.status);
    },
  },
  watch: {
    editedEntity: {
      immediate: true,
      handler(val) {
        if (val) {
          const localizedFields = ["name", "description", "suggestions", "risk", "auditorRecommendations", "level", "manager", "approver", "reporterNote"];
          this.form = {
            ...this.prepareFormForEdit(val, localizedFields),
            targetApprovalDate: val.targetApprovalDate || null,
            targetResolutionDate: val.targetResolutionDate || null,
            complianceItems: val.complianceItems || [],
            approvals: val.approvals || [],
          };

          if (val.owner) {
            if (val.owner.department) this.selectedOwnerType = "DEPARTMENT";
            else if (val.owner.process) this.selectedOwnerType = "PROCESS";
            else this.selectedOwnerType = "ORGANIZATION";
          } else {
            this.form.owner = { organization: this.currentOrganization };
            this.selectedOwnerType = "ORGANIZATION";
          }

          if (val.criticality) {
            this.selectedTargetIndex = val.criticality;
          }

          if (val.test) {
            this.form.test = val.test._id;
          } else {
            this.form.test = this.testId;
          }
          if (!this.form._id) {
            this.$set(this.form, "registerDate", new Date().toISOString().slice(0, 10));
            this.$set(this.form, "detectionDate", this.form.detectionDate || new Date().toISOString().slice(0, 10));
          }

          // Make sure refs are objects
          if (this.form.type && typeof this.form.type === "string") this.form.type = null;
          if (this.form.category && typeof this.form.category === "string") this.form.category = null;
          // Root causes usually array
          if (!this.form.rootCauses) this.form.rootCauses = [];

          // Try to set selectedTargetIndex
          if (!this.form.actions) this.$set(this.form, "actions", []);
          if (this.form.actions && Array.isArray(this.form.actions)) {
            // Localize action contents if needed
            this.form.actions = this.form.actions.map((a) => ({
              ...a,
              description: a.description?.[this.currentLanguage] || a.description,
              type: a.type?.[this.currentLanguage] || a.type,
            }));
          }
        } else {
          this.form = {
            control: this.controlId,
            test: this.testId,
            status: "AWAITING_APPROVAL",
            actions: [],
            targetApprovalDate: null,
            targetResolutionDate: null,
            complianceItems: [],
            owner: { organization: this.currentOrganization },
          };
          this.$set(this.form, "registerDate", new Date().toISOString().slice(0, 10));
          this.$set(this.form, "detectionDate", new Date().toISOString().slice(0, 10));

          if (this.initialControl) {
            this.form.control = this.initialControl;
            if (this.initialControl.complianceItems) {
              this.form.complianceItems = [...this.initialControl.complianceItems];
            }
          }

          this.selectedOwnerType = "ORGANIZATION";
          // If creating new, default to Organization owner
          this.onOrganizationSelect();
          this.selectedTargetIndex = null;
        }
      },
    },
    initialControl: {
      immediate: true,
      handler(val) {
        if (val && !this.form._id) {
          this.$set(this.form, "control", val);
          if (val.complianceItems) {
            this.$set(this.form, "complianceItems", [...val.complianceItems]);
          }
        }
      },
    },
  },
  created() {
    this.$store.dispatch("organizationFindingTarget/fetchTarget");
  },
  methods: {
    getApprovalStatusVariant(status) {
      const map = {
        PENDING: "warning",
        APPROVED: "success",
        REJECTED: "danger",
      };
      return map[status] || "secondary";
    },
    addControls(items) {
      const newItem = items[0];
      this.$set(this.form, "control", newItem);
      if (newItem.complianceItems) {
        this.$set(this.form, "complianceItems", [...newItem.complianceItems]);
      }
    },
    onCriticalityChange(key) {
      if (!key) return;

      const target = this.findingTargets[key];

      if (target) {
        // 1. Set Criticality
        this.form.criticality = key.toUpperCase();

        // 3. Calculate Target Dates
        const calculateDate = (duration) => {
          if (!duration || duration.value === null || duration.value === undefined || !duration.unit) {
            return null;
          }
          const { value, unit } = duration;
          const numValue = Number(value);
          const date = new Date();
          const upperUnit = unit.toUpperCase();
          if (upperUnit === "WEEK") date.setDate(date.getDate() + numValue * 7);
          else if (upperUnit === "MONTH") date.setMonth(date.getMonth() + numValue);
          else if (upperUnit === "DAY") date.setDate(date.getDate() + numValue);
          return date;
        };

        const approvalDate = calculateDate(target.approvalDuration);
        const resolutionDate = calculateDate(target.resolutionDuration);

        this.$set(this.form, "targetApprovalDate", approvalDate);
        this.$set(this.form, "targetResolutionDate", resolutionDate);
      }
    },
    onAdminSelect(admin) {
      this.form.admin = admin;
    },
    // Library Search Methods
    async onTypeSearch(value, callback) {
      const response = await this.$store.dispatch(`workspaceLibraryFindingType/paginate`, {
        options: { limit: 50, sort: { [`name.${this.currentLanguage}`]: 1 } },
        query: { name: { value } },
      });
      callback(response.data?.docs ?? []);
    },
    onTypeSelect(value) {
      this.form.type = value;
    },
    async onRootCauseSearch(value, callback) {
      const response = await this.$store.dispatch(`workspaceLibraryFindingRootCause/paginate`, {
        options: { limit: 50, sort: { [`name.${this.currentLanguage}`]: 1 } },
        query: { name: { value } },
      });
      callback(response.data?.docs ?? []);
    },
    onRootCauseSelect(value) {
      this.form.rootCauses = value;
    },
    async onCategorySearch(value, callback) {
      const response = await this.$store.dispatch(`workspaceLibraryFindingCategory/paginate`, {
        options: { limit: 50, sort: { [`name.${this.currentLanguage}`]: 1 } },
        query: { name: { value } },
      });
      callback(response.data?.docs ?? []);
    },
    onCategorySelect(value) {
      this.form.category = value;
    },

    // Owner Selection Methods
    ownerChanged(e) {
      if (e.target.value == "ORGANIZATION") {
        this.onOrganizationSelect();
      }
      setTimeout(() => {
        this.$refs?.departmentFilter?.reset();
        this.$refs?.processFilter?.reset();
      }, 500);
    },
    onOrganizationSelect() {
      const owner = { organization: this.currentOrganization };
      this.form = { ...this.form, owner };
    },
    customLabel(value) {
      return this.$options.filters.translate(value.name);
    },
    async onDepartmentSearch(value, callback) {
      const response = await this.$store.dispatch(`organizationDepartment/paginate`, {
        options: { limit: 50, sort: { [`name.${this.currentLanguage}`]: 1 } },
        query: { name: { value } },
      });
      callback(response.data?.docs ?? []);
    },
    onDepartmentSelect(department) {
      const owner = { department };
      this.form = { ...this.form, owner };
    },
    async onProcessSearch(value, callback) {
      const response = await this.$store.dispatch(`organizationProcess/paginate`, {
        options: { limit: 50, sort: { [`name.${this.currentLanguage}`]: 1 } },
        query: { name: { value } },
      });
      callback(response.data?.docs ?? []);
    },
    onProcessSelect(process) {
      const owner = { process };
      this.form = { ...this.form, owner };
    },

    complianceItemSelected(selections) {
      this.form = {
        ...this.form,
        complianceItems: [...selections],
      };
    },

    addInformedPerson(person) {
      if (!person) return;
      if (!this.form.informedPeople) this.$set(this.form, "informedPeople", []);

      // Check for duplicates
      if (this.form.informedPeople.some((p) => p._id === person._id)) {
        this.personSelectValue = null;
        return;
      }

      this.form.informedPeople.push(person);
      this.$nextTick(() => {
        this.personSelectValue = null;
      });
    },
    removeInformedPerson(index) {
      this.form.informedPeople.splice(index, 1);
    },
    addAction() {
      this.form.actions.push({ description: {}, type: {} });
    },
    removeAction(index) {
      this.form.actions.splice(index, 1);
    },

    async requestApproval() {
      if (!confirm(this.$t("common.areYouSure"))) return;
      try {
        await this.$store.dispatch("organizationFinding/requestApproval", { findingId: this.form._id });
        this.$toast.success(this.$t("common.success"));
        this.$emit("onSave", this.form);
        this.$root.$emit("bv::toggle::collapse", "sidebar-new-finding");
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error);
      }
    },
    async save() {
      // Basic validation
      if (!this.form.name || !this.form.description || !this.form.registerDate || !this.form.detectionDate) {
        this.$toast.warning(this.$t("common.validation-error"));
        return;
      }

      if (this.selectedTargetIndex === null || this.selectedTargetIndex === undefined) {
        this.$toast.warning(this.$t("common.validation-error"));
        return;
      }

      try {
        const { data } = await this.$store.dispatch("organizationFinding/save", this.form);
        this.$toast.success(this.$t("common.saved"));

        this.$emit("onSave", data);
        this.$emit("save", data);
        this.form = {};
        this.$root.$emit("bv::toggle::collapse", "sidebar-new-finding");
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error);
      }
    },
  },
};
</script>
